import { forwardRef } from 'react';

import { Select, SelectItem } from '@heroui/react';

import { LoaderOption } from './components/loader-option';
import { OptionContent } from './components/option-content';
import { ScrollTrigger } from './components/scroll-trigger';
import { SelectedValue } from './components/selected-value';
import { DEFAULT_LOAD_MORE_THRESHOLD, LOADER_KEY, OPTION_STYLES, SELECT_STYLES } from './constants';
import { useLoadMore } from './hooks/use-load-more';
import type { InfiniteSelectProps, SelectOption } from './types';

export const InfiniteSelect = forwardRef<HTMLSelectElement, InfiniteSelectProps>(
  (
    {
      label,
      placeholder,
      options,
      error,
      className,
      onLoadMore,
      hasMore = false,
      isLoadingMore = false,
      loadMoreThreshold = DEFAULT_LOAD_MORE_THRESHOLD,
      ...props
    },
    ref,
  ) => {
    const { handleLoadMore } = useLoadMore({ onLoadMore, hasMore, isLoadingMore });

    // Helper: Find selected option
    const getSelectedOption = (value: string | undefined): SelectOption | null => {
      if (!value) return null;
      return options.find((option) => option.value === value) || null;
    };

    // Calculate trigger position
    const triggerIndex = Math.max(0, options.length - loadMoreThreshold);

    return (
      <div className="h-fit">
        {label && <label className="block text-sm font-medium text-muted-foreground">{label}</label>}

        <Select
          ref={ref}
          placeholder={placeholder}
          variant="bordered"
          aria-label={label || placeholder || 'Select option'}
          className={`cursor-pointer ${className || ''}`}
          classNames={SELECT_STYLES}
          isInvalid={!!error}
          renderValue={(items) => {
            const selectedItem = items[0];
            const selectedOption = getSelectedOption(selectedItem?.key as string);
            return <SelectedValue option={selectedOption} placeholder={placeholder} />;
          }}
          {...props}
        >
          {[
            ...options.map((option, index) => (
              <SelectItem
                key={option.value}
                textValue={option.label}
                className={OPTION_STYLES.default}
                hideSelectedIcon={true}
              >
                <>
                  {/* Insert scroll trigger at threshold position */}
                  {onLoadMore && index === triggerIndex && hasMore && (
                    <ScrollTrigger onInView={handleLoadMore} />
                  )}

                  <OptionContent option={option} />
                </>
              </SelectItem>
            )),

            ...(hasMore
              ? [
                  <SelectItem
                    key={LOADER_KEY}
                    textValue="Loading more"
                    className={OPTION_STYLES.loader}
                    isReadOnly
                  >
                    <LoaderOption isLoading={isLoadingMore} />
                  </SelectItem>,
                ]
              : []),
          ]}
        </Select>
      </div>
    );
  },
);

InfiniteSelect.displayName = 'InfiniteSelect';

// Re-export types for convenience
export type { InfiniteSelectProps, SelectOption } from './types';
